import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Box, type SxProps, type Theme } from "@mui/material";

interface MarkdownContentProps {
    content: string | null | undefined;
    sx?: SxProps<Theme>;
}

// Headings are demoted for visual sizing inside compact admin panels
// (drawers, cards) — no public SEO/heading-hierarchy stakes here like the
// storefront's MarkdownContent has, this just keeps a stray "# Heading"
// from rendering as a huge browser-default <h1> inside a small Paper card.
// react-markdown passes an internal `node` (AST reference) prop to every
// component — it must be destructured out, not spread onto the DOM
// element, or React renders it as a literal node="[object Object]"
// attribute.
const MARKDOWN_COMPONENTS: Components = {
    h1: function MarkdownH3({ node, children, ...props }) {
        return <h3 {...props}>{children}</h3>;
    },
    h2: function MarkdownH4({ node, children, ...props }) {
        return <h4 {...props}>{children}</h4>;
    },
    h3: function MarkdownH5({ node, children, ...props }) {
        return <h5 {...props}>{children}</h5>;
    },
    h4: function MarkdownH6a({ node, children, ...props }) {
        return <h6 {...props}>{children}</h6>;
    },
    h5: function MarkdownH6b({ node, children, ...props }) {
        return <h6 {...props}>{children}</h6>;
    },
    h6: function MarkdownH6c({ node, children, ...props }) {
        return <h6 {...props}>{children}</h6>;
    },
    // No image-insertion UI exists in the product/category forms — keep
    // this preview matching exactly what customers see on the storefront,
    // which also never renders an image from description markdown.
    img: function MarkdownImg() {
        return null;
    },
};

// Small, self-contained styling rather than remapping every element to a
// matching MUI component — this is a read-only preview inside an existing
// Paper card, not a primary UI surface, so plain themed CSS is enough to
// look native without the extra code a full MUI-component remap would add.
export default function MarkdownContent({ content, sx }: MarkdownContentProps) {
    if (!content) return null;

    return (
        <Box
            sx={{
                fontSize: "0.875rem",
                lineHeight: 1.7,
                "& h3, & h4, & h5, & h6": {
                    fontWeight: 600,
                    mt: 1.5,
                    mb: 0.5,
                },
                "& p": { mb: 1 },
                "& ul, & ol": { pl: 3, mb: 1 },
                "& li": { mb: 0.25 },
                "& table": {
                    borderCollapse: "collapse",
                    width: "100%",
                    mb: 1,
                },
                "& th, & td": {
                    border: "1px solid",
                    borderColor: "divider",
                    p: 0.75,
                    textAlign: "left",
                },
                "& code": {
                    bgcolor: "action.hover",
                    px: 0.5,
                    borderRadius: 0.5,
                    fontSize: "0.85em",
                },
                "& a": { color: "primary.main" },
                ...sx,
            }}
        >
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={MARKDOWN_COMPONENTS}
            >
                {content}
            </ReactMarkdown>
        </Box>
    );
}
